/**
 * Phase 4.5 — numbered-priority picker for auto-retry tectonic models.
 *
 * Renders the 3 non-primary models as a checkbox list. Clicking a
 * checkbox toggles membership; the priority badge to the right of
 * each selected row shows its order in the selection (1, 2, 3…).
 * Unselecting a row renumbers the remaining selections down so the
 * priority sequence stays gap-free.
 *
 * Visual idiom follows the Phase-4.13.2 design alignment: parchment
 * surface, soft 1px outline, JetBrains Mono throughout, uppercase
 * --ink-3 mini-label for the priority badge (no saturated accent
 * fill). Matches the BatchModal's existing ``modal-field`` rhythm
 * so the picker sits inside the form without visual rupture.
 */

import type { TectonicModelCode } from '../api/types';
import { TECTONIC_MODEL_LABELS } from '../api/types';
import './RetryModelPicker.css';

const ALL_MODELS: readonly TectonicModelCode[] = [
  'NNR_MORVEL56',
  'NUVEL_1A',
  'ITRF2014',
  'GRSM_21',
] as const;

interface Props {
  /** The PRIMARY model the operator picked in the dropdown above;
   *  excluded from the picker (a retry with the same model would
   *  just re-fail). */
  excludedModel: TectonicModelCode;
  /** Currently-selected models in priority order. ``[]`` when no
   *  selections yet. */
  selectedOrder: TectonicModelCode[];
  /** Called with the new order on every toggle. Parent owns state. */
  onChange: (order: TectonicModelCode[]) => void;
}

export function RetryModelPicker({
  excludedModel,
  selectedOrder,
  onChange,
}: Props) {
  const available = ALL_MODELS.filter((m) => m !== excludedModel);

  const toggle = (model: TectonicModelCode) => {
    if (selectedOrder.includes(model)) {
      onChange(selectedOrder.filter((m) => m !== model));
    } else {
      onChange([...selectedOrder, model]);
    }
  };

  return (
    <div className="retry-model-picker" data-testid="retry-model-picker">
      {available.map((model) => {
        const priority = selectedOrder.indexOf(model);
        const selected = priority >= 0;
        return (
          <label
            key={model}
            className={`retry-model-item ${selected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={selected}
              onChange={() => toggle(model)}
              data-testid={`retry-model-checkbox-${model}`}
              className="gnss-qa-checkbox"
            />
            <span className="model-name">{TECTONIC_MODEL_LABELS[model]}</span>
            {selected && (
              <span
                className="priority-badge"
                aria-label={`приоритет ${priority + 1}`}
                data-testid={`retry-model-priority-${model}`}
              >
                {priority + 1}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
